import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-[hsl(46,65%,52%)]/10 animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-serif italic text-xl font-medium uppercase tracking-wider text-primary transition-colors hover:text-secondary hover:drop-shadow-[0_0_8px_hsla(46,65%,52%,0.3)] pt-4 inline-block">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems
  } = useMenu();
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-40 flex flex-col bg-[hsl(40,30%,98%)]/98 backdrop-blur-xl pt-24 px-6 lg:hidden animate-in slide-in-from-top-10 fade-in duration-300">
      <nav className="flex flex-col gap-6 items-center">
        {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="font-serif italic text-2xl font-medium uppercase tracking-wider text-primary hover:text-secondary transition-colors pt-8 inline-block" onClick={onClose} data-index={i}>
            {item.label}
          </Link>)}
        <Button asChild variant="outline" className="mt-8 border-secondary text-primary hover:bg-secondary hover:text-background w-full max-w-xs font-serif italic text-base">
          <Link to="/ContactUs">
            <Phone className="mr-2 h-4 w-4" />
            Call Now
          </Link>
        </Button>
      </nav>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 border-b border-transparent ${isScrolled ? "bg-[hsl(40,30%,98%)]/80 backdrop-blur-md shadow-lg border-[hsl(46,65%,52%)]/20 py-1" : "bg-[hsl(40,30%,98%)] py-2"}`}>
      <div className="container mx-auto px-4 md:px-6 flex items-center justify-start">
        
        <Link to="/HomePage#" className="relative z-50 flex-shrink-0 flex items-center">
          <img src="https://loved-mayfly.10web.cloud/wp-content/uploads/2026/03/Abracadabra-Events-Logo-Rectangular.png" alt="Abracadabra Events" className="logo transition-all duration-500 object-contain !w-[100px] !h-auto" />
        </Link>

        
        <div className="hidden lg:flex items-center gap-8 ml-auto">
          <MenuProvider menu_id="17">
            <DesktopMenu />
          </MenuProvider>

          <Button asChild variant="outline" className="border border-secondary bg-transparent text-primary hover:bg-transparent hover:text-secondary hover:border-secondary hover:shadow-[0_0_15px_hsl(46,65%,52%)] transition-all duration-300 uppercase tracking-wider font-serif italic text-xl">
            <Link to="/ContactUs">
              <Phone className="mr-2 h-4 w-4" />
              Call Now
            </Link>
          </Button>
        </div>

        
        <Button variant="ghost" size="icon" className="lg:hidden relative z-50 text-primary hover:text-secondary hover:bg-transparent transition-colors p-0 h-auto w-auto" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="h-8 w-8" /> : <Menu className="h-8 w-8" />}
        </Button>

        
        <MenuProvider menu_id="17">
          <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
        </MenuProvider>
      </div>
    </header>;
}